/**
 * RoyaleEndScreen
 *
 * The persistent end-of-race banner shown ABOVE the (final) board once a Puzzle
 * Royale ends, and stays after the result modal is dismissed (web does NOT
 * navigate away on end). Compact + polished: a teal header strip with a trophy,
 * "Puzzle Royale ended", and your placement chip; a row of stat pills (rank /
 * points / solved / best combo); and a tidy action row (host Rematch +
 * View results + Exit). Presentational — parent passes my final row.
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';

import type {PlayerRoyaleState} from '@/Screens/FunChallenges/royaleTypes';

const TEAL = '#3DAB9E';
const TEAL_DARK = '#2E8B7F';
const BROWN = '#4D3F37';
const TEXT_MUTED = '#8A7E5F';
const CREAM3 = '#EDE4CC';
const PILL_BG = '#F6F1E4';

interface Props {
  me?: PlayerRoyaleState | null;
  myRank?: number;
  totalPlayers: number;
  /**
   * SOLO Puzzle Royale (1-player). Drops the placement/"you won" framing (there
   * are no opponents to beat) — the end card is just "Time's up!" + your stats.
   */
  solo?: boolean;
  /** Host-only: show the Rematch button (server pushes a new lobby to all). */
  canRematch?: boolean;
  onRematch?: () => void;
  /** Reopen the full result modal (standings). */
  onViewResults?: () => void;
  /** Leave the battle screen (player → Fun Challenges; spectator → stop). */
  onExit?: () => void;
  /** When set (e.g. spectators, who have no `me`), render inline standings with
   *  points + best combo per player instead of a lone header (#3). */
  players?: Record<string, PlayerRoyaleState>;
  leaderboard?: string[];
}

export default function RoyaleEndScreen({
  me,
  myRank,
  totalPlayers,
  solo = false,
  canRematch,
  onRematch,
  onViewResults,
  onExit,
  players,
  leaderboard,
}: Props) {
  const placed = me && myRank != null;
  const won = placed && myRank === 0;
  const showStandings = !!players && !!leaderboard && leaderboard.length > 0;

  return (
    <View style={styles.block}>
      {/* Teal header strip */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <CCText style={styles.trophy}>{solo ? '🏁' : won ? '🏆' : '🏁'}</CCText>
          <View>
            <CCText style={styles.title}>
              {solo ? "Time's up!" : 'Puzzle Royale ended'}
            </CCText>
            {/* Solo: no placement line (no opponents). Multiplayer: rank/won. */}
            {!solo && placed && (
              <CCText style={styles.subtitle}>
                {won ? 'You won!' : `You finished #${myRank! + 1}`}
              </CCText>
            )}
            {solo && me && (
              <CCText style={styles.subtitle}>
                {me.solved} solved · {me.points} points
              </CCText>
            )}
          </View>
        </View>
        {/* Rank chip is meaningless for a 1-player game — hide it for solo. */}
        {!solo && placed && (
          <View style={styles.rankChip}>
            <CCText style={styles.rankChipText}>
              #{myRank! + 1}
              <CCText style={styles.rankChipTotal}> / {totalPlayers}</CCText>
            </CCText>
          </View>
        )}
      </View>

      {/* Stat pills — players only (spectators read standings on the rail). */}
      {me && (
        <View style={styles.pills}>
          <StatPill label="Points" value={String(me.points)} />
          <StatPill label="Solved" value={String(me.solved)} />
          <StatPill label="Best combo" value={`x${me.bestCombo}`} />
        </View>
      )}

      {/* Inline standings — for spectators (no personal stats): winner-first
          list with points + best combo per player (#3). */}
      {showStandings && (
        <View style={styles.standings}>
          {leaderboard!.slice(0, 5).map((id, idx) => {
            const p = players![id];
            if (!p) return null;
            return (
              <View key={id} style={styles.standRow}>
                <View
                  style={[styles.standRank, idx === 0 && styles.standRankGold]}>
                  <CCText
                    style={[
                      styles.standRankText,
                      idx === 0 && styles.standRankTextGold,
                    ]}>
                    {idx + 1}
                  </CCText>
                </View>
                <CCText style={styles.standName} numberOfLines={1}>
                  {p.name || 'Player'}
                </CCText>
                <CCText style={styles.standCombo}>⚡{p.bestCombo}</CCText>
                <CCText style={styles.standPts}>{p.points} pts</CCText>
              </View>
            );
          })}
        </View>
      )}

      {/* Actions */}
      <View style={styles.actions}>
        {canRematch && (
          <Pressable onPress={onRematch} style={styles.rematchBtn}>
            <CCText style={styles.rematchText}>Rematch</CCText>
          </Pressable>
        )}
        {onViewResults && (
          <Pressable onPress={onViewResults} style={styles.secondaryBtn}>
            <CCText style={styles.secondaryText}>View results</CCText>
          </Pressable>
        )}
        {onExit && (
          <Pressable onPress={onExit} style={styles.secondaryBtn}>
            <CCText style={styles.secondaryText}>Exit</CCText>
          </Pressable>
        )}
      </View>
      {canRematch && (
        <CCText style={styles.rematchHint}>
          Rematch starts a fresh race with the same settings — everyone here
          joins automatically.
        </CCText>
      )}
    </View>
  );
}

function StatPill({label, value}: {label: string; value: string}) {
  return (
    <View style={styles.pill}>
      <CCText style={styles.pillValue}>{value}</CCText>
      <CCText style={styles.pillLabel}>{label}</CCText>
    </View>
  );
}

const styles = StyleSheet.create({
  block: {
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: CREAM3,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 3},
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: TEAL,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  headerLeft: {flexDirection: 'row', alignItems: 'center', gap: 12, flexShrink: 1},
  trophy: {fontSize: FontSizes[26] || 26},
  title: {fontSize: FontSizes[18] || 18, fontWeight: '800', color: '#fff'},
  subtitle: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.9)',
    marginTop: 1,
  },
  rankChip: {
    backgroundColor: TEAL_DARK,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
  },
  rankChipText: {fontSize: FontSizes[16] || 16, fontWeight: '800', color: '#fff'},
  rankChipTotal: {fontSize: FontSizes[12] || 12, fontWeight: '700', color: 'rgba(255,255,255,0.8)'},

  pills: {flexDirection: 'row', gap: 10, paddingHorizontal: 16, paddingTop: 14},
  pill: {
    flex: 1,
    backgroundColor: PILL_BG,
    borderRadius: 12,
    paddingVertical: 10,
    alignItems: 'center',
  },
  pillValue: {fontSize: FontSizes[20] || 20, fontWeight: '800', color: BROWN},
  pillLabel: {
    fontSize: FontSizes[10] || 10,
    fontWeight: '700',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginTop: 2,
  },

  standings: {paddingHorizontal: 16, paddingTop: 14, gap: 6},
  standRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
  standRank: {
    width: 22,
    height: 22,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CREAM3,
  },
  standRankGold: {backgroundColor: '#E7B325'},
  standRankText: {fontSize: FontSizes[12] || 12, fontWeight: '800', color: BROWN},
  standRankTextGold: {color: '#fff'},
  standName: {flex: 1, fontSize: FontSizes[14] || 14, fontWeight: '700', color: BROWN},
  standCombo: {fontSize: FontSizes[12] || 12, fontWeight: '700', color: TEXT_MUTED},
  standPts: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: TEAL},
  actions: {flexDirection: 'row', gap: 10, paddingHorizontal: 16, paddingTop: 14, paddingBottom: 6},
  rematchBtn: {
    flex: 1,
    backgroundColor: TEAL,
    borderRadius: 999,
    paddingVertical: 11,
    alignItems: 'center',
  },
  rematchText: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: '#fff'},
  secondaryBtn: {
    flex: 1,
    backgroundColor: PILL_BG,
    borderRadius: 999,
    paddingVertical: 11,
    alignItems: 'center',
  },
  secondaryText: {fontSize: FontSizes[13] || 13, fontWeight: '800', color: BROWN},
  rematchHint: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    paddingHorizontal: 16,
    paddingBottom: 14,
  },
});
